// @flow
import * as React from 'react';
import styled from 'styled-components';
import { positionTooltip, TooltipPopup, useTooltip } from '@reach/tooltip';
import '@reach/tooltip/styles.css';
import { animated, useSpring } from '@react-spring/web';
import Theme, { type TooltipContentTheme } from './theme';
import { bodyStyles } from '../../internal/internal-typography';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

type IconTooltipProps = {|
  /**
   * Represents the text that is displayed within the tooltip content.
   */
  label: string,
  /**
   * The accessibility label for assistive technology users.
   */
  ariaLabel: string,
  /**
   * The icon that serves as a trigger for the tooltip when hovered/focused.
   */
  icon: React.AbstractComponent<any, any>,
  /**
   * Allow click events to propagate.
   *
   * Typically tooltips are used for non-interactive elements. If click events are enabled, then it's easy for a user to
   * hover over a tooltip trigger and then click before the tooltip animates in - closing the tooltip before it appears.
   *
   * Because of this we do not allow propagation by default - however, there are cases where a tooltip is used on a menu
   * trigger or other interactive input, so this prop allows click events in those cases.
   */
  propagateClickEvent?: boolean,
  ...DataProvidesProps,
|};

type TooltipProps = {|
  /**
   * The custom React component that serves as a trigger for the tooltip when hovered/focused.
   */
  children: React.Element<any>,
  /**
   * Represents the text that is displayed within the tooltip content.
   */
  label: string,
  /**
   * The accessibility label for assistive technology users.
   */
  ariaLabel: string,
  /**
   * Allow click events to propagate.
   *
   * Typically tooltips are used for non-interactive elements. If click events are enabled, then it's easy for a user to
   * hover over a tooltip trigger and then click before the tooltip animates in - closing the tooltip before it appears.
   * However, there are cases where a tooltip is used on a menu trigger or other interactive input, so we need to
   * support click events in those cases.
   */
  propagateClickEvent?: boolean,
  ...DataProvidesProps,
|};

type ThemeableTooltipProps = {
  ...TooltipProps,
  theme: TooltipContentTheme,
};

const ThemeableTooltipPopupContent = animated(styled(TooltipPopup)`
  &[data-reach-tooltip] {
    ${bodyStyles};
    color: ${({ theme }) => theme.color};
    background-color: ${({ theme }) => theme.backgroundColor};
    border: none;
    border-radius: 0.3125rem;
    box-shadow: 0 2px 6px ${({ theme }) => theme.boxShadowColor};
    box-sizing: border-box;
    font-size: 1rem;
    padding: ${({ theme }) => theme.padding};
    white-space: normal;
    max-width: 200px;
    overflow-wrap: break-word;
    width: fit-content;
  }
`);

// Center the tooltip, but collisions will win
const centered = (triggerRect, tooltipRect) => {
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const left = triggerCenter - tooltipRect.width / 2;
  const maxLeft = window.innerWidth - tooltipRect.width - 2;

  const positionTooltipResults = positionTooltip(triggerRect, tooltipRect);

  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: positionTooltipResults.top,
  };
};

const TriggerWrapper = styled.span`
  display: inline-flex;
`;

const FlexContainer = styled.span`
  display: flex;
`;

const ThemeableTooltip = (props: ThemeableTooltipProps) => {
  const { children, label, ariaLabel, propagateClickEvent, theme } = props;

  const eventHandler = (event) => {
    if (!propagateClickEvent) {
      event.preventDefault();
    }
  };

  const [trigger, tooltip, isVisible] = useTooltip({
    onMouseDown: eventHandler,
    onPointerDown: eventHandler,
  });
  const springStyles = useSpring({
    opacity: isVisible ? '1' : '0',
    display: isVisible ? 'inherit' : 'none',
  });

  return (
    <React.Fragment>
      <FlexContainer>
        <TriggerWrapper {...trigger} {...getDataProvidesProps(props)}>
          {React.cloneElement(children)}
        </TriggerWrapper>
      </FlexContainer>
      <ThemeableTooltipPopupContent
        {...tooltip}
        theme={theme}
        // control visibility through opacity for animation purposes
        isVisible={true}
        label={label}
        aria-label={ariaLabel}
        position={centered}
        style={springStyles}
      />
    </React.Fragment>
  );
};

const Tooltip = (props: TooltipProps) => (
  <ThemeableTooltip {...props} theme={Theme.tooltip.default}>
    {props.children}
  </ThemeableTooltip>
);
Tooltip.defaultProps = { 'data-provides': 'tooltip' };

const IconTooltip = (props: IconTooltipProps) => (
  <ThemeableTooltip {...props} theme={Theme.tooltip.icon}>
    {React.createElement(props.icon)}
  </ThemeableTooltip>
);
IconTooltip.defaultProps = { 'data-provides': 'icon-tooltip' };

const CustomIconTooltip = (props: TooltipProps) => (
  <ThemeableTooltip {...props} theme={Theme.tooltip.icon}>
    {props.children}
  </ThemeableTooltip>
);
CustomIconTooltip.defaultProps = { 'data-provides': 'icon-tooltip' };

export { Tooltip, IconTooltip, CustomIconTooltip };
